/**
 * What an email carries, independent of how it reaches Outlook.
 *
 * A small block model rather than a copy of the page's markup: Outlook drops
 * class-based CSS and most layout, so the body is rendered here with inline
 * styles from plain records, and every string is escaped on the way out.
 */

export interface TextRun {
  text: string;
  bold?: boolean;
}

export type EmailBlock =
  | { kind: "heading"; text: string }
  | { kind: "paragraph"; runs: TextRun[] }
  | { kind: "list"; ordered: boolean; items: TextRun[][] }
  | { kind: "table"; columns: string[]; rows: string[][]; note?: string }
  | { kind: "image"; id: string; dataUrl: string; alt: string };

export interface EmailDocument {
  title: string;
  blocks: EmailBlock[];
  /** Provenance lines: which context and version, and when the data was read. */
  footer: string[];
}

export type ImageBlock = Extract<EmailBlock, { kind: "image" }>;

/** Resolves where an image's bytes live: a data URL inline, or a MIME cid. */
export type ImageSource = (image: ImageBlock) => string;

export const inlineDataUrl: ImageSource = (image) => image.dataUrl;

const FONT = "font-family:Segoe UI,Calibri,Arial,sans-serif;";
const STYLE = {
  title: `${FONT}font-size:20px;font-weight:600;color:#17365D;margin:0 0 12px;`,
  heading: `${FONT}font-size:15px;font-weight:600;color:#17365D;margin:16px 0 6px;`,
  paragraph: `${FONT}font-size:14px;line-height:1.45;color:#1F2933;margin:0 0 10px;`,
  list: `${FONT}font-size:14px;line-height:1.45;color:#1F2933;margin:0 0 10px;padding-left:22px;`,
  table: "border-collapse:collapse;margin:0 0 6px;",
  th: `${FONT}font-size:12px;font-weight:600;color:#FFFFFF;background:#17365D;border:1px solid #D5DCE6;padding:5px 8px;text-align:left;`,
  td: `${FONT}font-size:12px;color:#1F2933;border:1px solid #D5DCE6;padding:5px 8px;`,
  note: `${FONT}font-size:11px;color:#5B6675;margin:0 0 12px;`,
  image: "display:block;max-width:640px;width:100%;height:auto;margin:4px 0 12px;",
  footer: `${FONT}font-size:11px;color:#5B6675;margin:16px 0 0;border-top:1px solid #D5DCE6;padding-top:8px;`,
} as const;

export function escapeHtml(value: string): string {
  return value
    .replace(/&/g, "&amp;")
    .replace(/</g, "&lt;")
    .replace(/>/g, "&gt;")
    .replace(/"/g, "&quot;")
    .replace(/'/g, "&#39;");
}

function runsHtml(runs: TextRun[]): string {
  return runs
    .map((run) =>
      run.bold ? `<strong>${escapeHtml(run.text)}</strong>` : escapeHtml(run.text),
    )
    .join("");
}

function runsText(runs: TextRun[]): string {
  return runs.map((run) => run.text).join("");
}

function tableHtml(block: Extract<EmailBlock, { kind: "table" }>): string {
  const head = block.columns
    .map((column) => `<th style="${STYLE.th}">${escapeHtml(column)}</th>`)
    .join("");
  const body = block.rows
    .map(
      (row) =>
        `<tr>${block.columns
          .map(
            (_column, index) =>
              `<td style="${STYLE.td}">${escapeHtml(row[index] ?? "")}</td>`,
          )
          .join("")}</tr>`,
    )
    .join("");
  const note = block.note
    ? `<p style="${STYLE.note}">${escapeHtml(block.note)}</p>`
    : "";
  return (
    `<table role="presentation" cellpadding="0" cellspacing="0" style="${STYLE.table}">` +
    `<thead><tr>${head}</tr></thead><tbody>${body}</tbody></table>${note}`
  );
}

function blockHtml(block: EmailBlock, imageSource: ImageSource): string {
  switch (block.kind) {
    case "heading":
      return `<p style="${STYLE.heading}">${escapeHtml(block.text)}</p>`;
    case "paragraph":
      return `<p style="${STYLE.paragraph}">${runsHtml(block.runs)}</p>`;
    case "list": {
      const tag = block.ordered ? "ol" : "ul";
      const items = block.items.map((item) => `<li>${runsHtml(item)}</li>`).join("");
      return `<${tag} style="${STYLE.list}">${items}</${tag}>`;
    }
    case "table":
      return tableHtml(block);
    case "image":
      return (
        `<img src="${escapeHtml(imageSource(block))}" alt="${escapeHtml(block.alt)}" ` +
        `style="${STYLE.image}" />`
      );
  }
}

/** The HTML fragment pasted into, or embedded in, the message body. */
export function renderEmailHtml(
  document: EmailDocument,
  imageSource: ImageSource = inlineDataUrl,
): string {
  const footer = document.footer.length
    ? `<p style="${STYLE.footer}">${document.footer.map(escapeHtml).join("<br />")}</p>`
    : "";
  return (
    `<div>` +
    `<p style="${STYLE.title}">${escapeHtml(document.title)}</p>` +
    document.blocks.map((block) => blockHtml(block, imageSource)).join("") +
    footer +
    `</div>`
  );
}

function tableText(block: Extract<EmailBlock, { kind: "table" }>): string {
  const lines = [
    block.columns.join(" | "),
    ...block.rows.map((row) =>
      block.columns.map((_column, index) => row[index] ?? "").join(" | "),
    ),
  ];
  if (block.note) lines.push(block.note);
  return lines.join("\n");
}

/** The plain-text alternative, for clients and paste targets without HTML. */
export function renderEmailText(document: EmailDocument): string {
  const parts: string[] = [document.title];
  for (const block of document.blocks) {
    switch (block.kind) {
      case "heading":
        parts.push(block.text);
        break;
      case "paragraph":
        parts.push(runsText(block.runs));
        break;
      case "list":
        parts.push(
          block.items
            .map((item, index) =>
              `${block.ordered ? `${index + 1}.` : "-"} ${runsText(item)}`,
            )
            .join("\n"),
        );
        break;
      case "table":
        parts.push(tableText(block));
        break;
      case "image":
        parts.push(`[Chart: ${block.alt}]`);
        break;
    }
  }
  if (document.footer.length) parts.push(document.footer.join("\n"));
  return parts.join("\n\n");
}
